import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { branchesApi, warehousesApi, type WarehouseType } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const TYPES: WarehouseType[] = ["Main", "Satellite", "Cold", "Quarantine", "Consignment"];

interface FormState {
  id?: string; code: string; name: string;
  warehouseType: WarehouseType; branchId: string; isActive: boolean;
}
const blank = (): FormState => ({
  code: "", name: "", warehouseType: "Main", branchId: "", isActive: true,
});

/**
 * UC warehouse.warehouse.* — Warehouse CRUD. Type drives downstream behaviour
 * (Quarantine = no issue until QC release; Cold = temp alerts in Final, etc.).
 * Delete refuses when any ledger row exists for the warehouse.
 */
export function WarehousesPage() {
  const qc = useQueryClient();
  const canCreate = useAuth(s => s.hasPermission("warehouse.warehouse.create"));
  const canEdit   = useAuth(s => s.hasPermission("warehouse.warehouse.edit"));
  const canDelete = useAuth(s => s.hasPermission("warehouse.warehouse.delete"));

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(blank());
  const [error, setError] = useState<string | null>(null);

  const branches = useQuery({ queryKey: ["branches"], queryFn: () => branchesApi.list() });
  const query = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
  });

  const startCreate = () => {
    setForm({
      ...blank(),
      branchId: branches.data?.[0]?.id ?? "",
    });
    setError(null);
    setOpen(true);
  };
  const startEdit = async (id: string) => {
    const w = await warehousesApi.get(id);
    setForm({
      id: w.id, code: w.code, name: w.name,
      warehouseType: w.warehouseType, branchId: w.branchId, isActive: w.isActive,
    });
    setError(null); setOpen(true);
  };

  const saveMutation = useMutation({
    meta: { successMessage: "Warehouse saved" },
    mutationFn: async () => {
      if (form.id) {
        await warehousesApi.update(form.id, {
          id: form.id, code: form.code, name: form.name,
          warehouseType: form.warehouseType, isActive: form.isActive,
        });
      } else {
        await warehousesApi.create({
          code: form.code, name: form.name,
          warehouseType: form.warehouseType, branchId: form.branchId,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warehouses"] });
      setOpen(false);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => warehousesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouses"] }),
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Warehouses</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate}>New warehouse</Button>
        )}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        {query.isLoading ? <CircularProgress /> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data?.items.map(w => (
                <TableRow key={w.id} hover>
                  <TableCell><b>{w.code}</b></TableCell>
                  <TableCell>{w.name}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={w.warehouseType} />
                  </TableCell>
                  <TableCell>{w.branchName}</TableCell>
                  <TableCell>
                    <Chip size="small" color={w.isActive ? "success" : "default"}
                      label={w.isActive ? "Active" : "Inactive"} />
                  </TableCell>
                  <TableCell align="right">
                    {canEdit && (
                      <IconButton size="small" onClick={() => startEdit(w.id)}><EditIcon fontSize="small" /></IconButton>
                    )}
                    {canDelete && (
                      <IconButton size="small" color="error"
                        onClick={() => { if (confirm(`Delete warehouse ${w.code}?`)) deleteMutation.mutate(w.id); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? `Edit warehouse ${form.code}` : "New warehouse"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Code *" value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))} />
            <TextField label="Name *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField select label="Type *" value={form.warehouseType}
              onChange={(e) => setForm(f => ({ ...f, warehouseType: e.target.value as WarehouseType }))}>
              {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            {!form.id && (
              <TextField select label="Branch *" value={form.branchId}
                onChange={(e) => setForm(f => ({ ...f, branchId: e.target.value }))}>
                {branches.data?.map(b => <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>)}
              </TextField>
            )}
            {form.id && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch checked={form.isActive} onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                <Typography>{form.isActive ? "Active" : "Inactive"}</Typography>
              </Stack>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {(!form.code || !form.name || (!form.id && !form.branchId)) && (
              <Alert severity="info">
                {!form.code ? "Code is required." : !form.name ? "Name is required." : "Pick a branch."}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained"
            disabled={!form.code || !form.name || (!form.id && !form.branchId) || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}>
            {form.id ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
